/**
 * Spec 052, step 6: «Заказы» and «Клиенты» — tabs of «Проекты» (the tab bar is full; Kostya,
 * 26.10: «давай в проектах направления»), and one client's page.
 *
 * «Заказы» is a board by the funnel's stages. On a computer a card is dragged to another stage
 * (Kostya, 26.10: «перетаскиванием на доске»); a column the move cannot reach does not take
 * it, «Потерян» asks why. On a phone the stages are chips over a list, and the stage changes in
 * the order itself — a finger cannot drag between columns that do not fit the screen.
 */
import { useMemo, useState, type DragEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";

import { VersionConflictError } from "@/api/client";
import { Clients, Orders, type ClientOut, type OrderOut } from "@/api/orders";
import { Agents, Areas } from "@/api/resources";
import type { AreaOut } from "@/api/types";
import { ClientAccessesBlock } from "@/components/ClientAccesses";
import { ClientDocumentsBlock } from "@/components/ClientDocuments";
import { ClientDossierBlock } from "@/components/ClientMemory";
import { ClientPassportBlock } from "@/components/ClientPassport";
import { Button } from "@/components/ui/Button";
import { SearchField, SelectField, TextArea, TextField } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { Mark } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { AppBar, Caption, Chip, EmptyNote, ScreenHeader, Switch, useIsDesktop } from "@/kit";
import { cn } from "@/lib/cn";
import { canMove, day, FINISHED, nextMilestone, OPEN_STAGES, rub, STAGE_LABEL, type Stage } from "@/lib/orders";
import { usePeople } from "@/lib/people";
import { useBackButton } from "@/lib/telegram-ui";

export const ORDERS_KEY = ["client-orders"] as const;

/** Spec 052, step 1: a direction where clients' orders live (custom development); games have none. */
type OrderArea = AreaOut & { takes_orders?: boolean };

/** Directions that take orders, live ones only. */
function useOrderAreas() {
  return useQuery({ queryKey: ["areas"], queryFn: () => Areas.list(), select: (areas: OrderArea[]) => areas.filter((a) => a.takes_orders && !a.archived_at) });
}

/** Staff who may lead an order: live cards that are not someone's helper. */
function useStaff() {
  return useQuery({ queryKey: ["agents", "staff"], queryFn: () => Agents.list(), select: (agents) => agents.filter((a) => !a.archived_at && !a.parent_id) });
}

/** What failed, in words: a version conflict means someone changed the order meanwhile. */
function failure(error: Error): string {
  return error instanceof VersionConflictError ? "Заказ изменился, пока ты смотрел. Обновили — попробуй ещё раз." : error.message;
}

/* ── «Заказы» ─────────────────────────────────────────────────────────────────────────── */

export function OrdersBoard() {
  const desktop = useIsDesktop();
  const qc = useQueryClient();
  const toast = useToast();
  const [area, setArea] = useState("");
  const [manager, setManager] = useState("");
  const [finished, setFinished] = useState(false);
  const [phoneStage, setPhoneStage] = useState<Stage>("lead");
  const [creating, setCreating] = useState(false);
  const [dragged, setDragged] = useState<OrderOut | null>(null);
  const [over, setOver] = useState<Stage | null>(null);
  const [losing, setLosing] = useState<OrderOut | null>(null);
  const areas = useOrderAreas();
  const staff = useStaff();
  const orders = useQuery({
    queryKey: [...ORDERS_KEY, { area, manager }],
    queryFn: () => Orders.list({ area_id: area, manager_agent_id: manager }),
    refetchInterval: 30_000,
  });
  const move = useMutation({
    mutationFn: ({ order, stage, reason }: { order: OrderOut; stage: Stage; reason?: string }) =>
      Orders.move(order.id, { version: order.version, stage, reason_md: reason ?? null }),
    onSuccess: (order) => { toast.success(`${order.label}: ${STAGE_LABEL[order.stage]}`); setLosing(null); },
    onError: (error: Error) => toast.error(failure(error)),
    onSettled: () => void qc.invalidateQueries({ queryKey: ORDERS_KEY }),
  });

  const stages: Stage[] = finished ? [...OPEN_STAGES, ...FINISHED] : OPEN_STAGES;
  const byStage = (stage: Stage) => (orders.data ?? []).filter((o) => o.stage === stage);

  const drop = (stage: Stage) => {
    const order = dragged;
    setDragged(null);
    setOver(null);
    if (!order || !canMove(order.stage, stage)) return;
    if (stage === "lost") setLosing(order);
    else move.mutate({ order, stage });
  };
  const onOver = (e: DragEvent, stage: Stage) => {
    if (!dragged || !canMove(dragged.stage, stage)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (over !== stage) setOver(stage);
  };

  const filters = (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto] lg:items-end">
      <SelectField label="Направление" value={area} onChange={setArea} placeholder="Все направления"
        options={(areas.data ?? []).map((a) => ({ value: a.id, label: a.name }))} />
      <SelectField label="Менеджер" value={manager} onChange={setManager} placeholder="Все менеджеры"
        options={(staff.data ?? []).map((a) => ({ value: a.id, label: a.name }))} />
      <label className="flex min-h-[46px] items-center gap-3 text-body text-p-text">
        <Switch on={finished} label="Показать закрытые и потерянные" onChange={() => setFinished(!finished)} />
        Закрытые и потерянные
      </label>
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-meta text-p-text-muted">
          {desktop ? "Перетащи карточку на другой этап. Куда нельзя — колонка не примет." : "Этап заказа меняется в его карточке."}
        </p>
        <Button size="compact" icon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={() => setCreating(true)}>Новый заказ</Button>
      </div>
      {filters}
      {orders.isLoading && <SkeletonList count={3} />}
      {orders.isError && <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void orders.refetch()}>Повторить</Button>}>Заказы не загрузились.</EmptyNote>}
      {orders.data && orders.data.length === 0 && !area && !manager && (
        <EmptyNote action={<Button size="compact" onClick={() => setCreating(true)}>Новый заказ</Button>}>
          Заказов пока нет. Заказ — работа для клиента в направлении, где бывают заказы.
        </EmptyNote>
      )}
      {orders.data && orders.data.length > 0 && (desktop ? (
        <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-3" role="list" aria-label="Доска заказов по этапам">
          {stages.map((stage) => {
            const items = byStage(stage);
            const allowed = dragged ? canMove(dragged.stage, stage) : false;
            return (
              <section
                key={stage}
                role="listitem"
                aria-label={`${STAGE_LABEL[stage]}: ${items.length}`}
                onDragOver={(e) => onOver(e, stage)}
                onDragLeave={() => over === stage && setOver(null)}
                onDrop={(e) => { e.preventDefault(); drop(stage); }}
                className={cn(
                  "flex w-[260px] shrink-0 flex-col gap-2 rounded-card border border-transparent bg-p-surface-2 p-2 transition-colors",
                  dragged && !allowed && dragged.stage !== stage && "opacity-55",
                  over === stage && "border-p-text",
                )}
              >
                <header className="flex items-baseline justify-between px-1 pt-1">
                  <Caption>{STAGE_LABEL[stage]}</Caption>
                  <span className="text-meta text-p-text-muted">{items.length}</span>
                </header>
                {items.map((o) => (
                  <OrderCard key={o.id} order={o} draggable
                    onDragStart={() => setDragged(o)} onDragEnd={() => { setDragged(null); setOver(null); }}
                    moving={move.isPending && move.variables?.order.id === o.id} />
                ))}
                {items.length === 0 && <p className="px-1 pb-2 text-meta text-p-text-muted">Пусто</p>}
              </section>
            );
          })}
        </div>
      ) : (
        <>
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {stages.map((stage) => (
              <Chip key={stage} on={phoneStage === stage} onClick={() => setPhoneStage(stage)}>
                {STAGE_LABEL[stage]} <span className="font-medium">{byStage(stage).length}</span>
              </Chip>
            ))}
          </div>
          <div className="flex flex-col gap-2">
            {byStage(phoneStage).map((o) => <OrderCard key={o.id} order={o} />)}
            {byStage(phoneStage).length === 0 && <EmptyNote>На этапе «{STAGE_LABEL[phoneStage]}» заказов нет.</EmptyNote>}
          </div>
        </>
      ))}
      <LostSheet key={losing?.id ?? "none"} order={losing} busy={move.isPending} onClose={() => setLosing(null)}
        onConfirm={(reason) => losing && move.mutate({ order: losing, stage: "lost", reason })} />
      <NewOrderSheet open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function OrderCard({ order: o, draggable = false, onDragStart, onDragEnd, moving = false }: {
  order: OrderOut; draggable?: boolean; onDragStart?: () => void; onDragEnd?: () => void; moving?: boolean;
}) {
  const next = nextMilestone(o.milestones);
  return (
    <Link
      to={`/orders/${o.id}`}
      draggable={draggable}
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", o.id); onDragStart?.(); }}
      onDragEnd={onDragEnd}
      aria-busy={moving || undefined}
      className={cn(
        "flex flex-col gap-1 rounded-card border border-p-border bg-p-surface px-3 py-2.5 press-surface",
        draggable && "cursor-grab active:cursor-grabbing",
        moving && "opacity-55",
      )}
    >
      <span className="flex items-baseline gap-2">
        <span className="shrink-0 text-meta font-semibold text-p-text-muted">{o.label}</span>
        {o.amount_rub != null && <span className="ml-auto shrink-0 text-meta text-p-text-muted">{rub(o.amount_rub)}</span>}
      </span>
      <span className="line-clamp-2 text-[15px] font-semibold leading-5 text-p-text">{o.title}</span>
      <span className="truncate text-meta text-p-text-muted">{o.client.name}{o.manager ? ` · ${o.manager.name}` : ""}</span>
      {o.stage === "lost" && o.lost_reason && <span className="line-clamp-2 text-meta text-p-text-muted">Причина: {o.lost_reason}</span>}
      {next && <span className="line-clamp-1 text-meta text-p-text">Дальше: {next.title}{next.planned_at ? ` · ${day(next.planned_at)}` : ""}</span>}
    </Link>
  );
}

/** «Потерян» needs a reason — the server refuses it without one. */
export function LostSheet({ order, busy, onClose, onConfirm }: { order: OrderOut | null; busy: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState("");
  return (
    <Sheet open={Boolean(order)} onClose={onClose} title={order ? `${order.label} потерян` : "Заказ потерян"}
      footer={<Button block disabled={!reason.trim()} loading={busy} loadingText="Сохраняем…" onClick={() => onConfirm(reason.trim())}>Отметить потерянным</Button>}>
      <TextArea label="Почему" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} autoFocus
        hint="Например: дорого, выбрали других, отложили. Если клиент вернётся, заказ можно вернуть в «Обсуждение»." />
    </Sheet>
  );
}

/* ── A new order ────────────────────────────────────────────────────────────────────── */

export function NewOrderSheet({ open, onClose, clientId }: { open: boolean; onClose: () => void; clientId?: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const people = usePeople();
  const areas = useOrderAreas();
  const staff = useStaff();
  const clients = useQuery({ queryKey: ["clients"], queryFn: () => Clients.list(), enabled: open });
  const [title, setTitle] = useState("");
  const [client, setClient] = useState(clientId ?? "");
  const [area, setArea] = useState("");
  const [manager, setManager] = useState("");
  const [owner, setOwner] = useState("");
  const [brief, setBrief] = useState("");
  const onlyArea = areas.data?.length === 1 ? areas.data[0].id : "";
  const create = useMutation({
    mutationFn: () => Orders.create({
      title: title.trim(), client_id: client, area_id: area || onlyArea, brief_md: brief.trim() || null,
      manager_agent_id: manager || null, owner_user_id: owner || null,
    }),
    onSuccess: (order) => {
      void qc.invalidateQueries({ queryKey: ORDERS_KEY });
      void qc.invalidateQueries({ queryKey: ["clients"] });
      toast.success(`${order.label} заведён`);
      onClose();
      navigate(`/orders/${order.id}`);
    },
    onError: (error: Error) => toast.error(error.message),
  });
  const ready = title.trim() && client && (area || onlyArea);
  return (
    <Sheet open={open} onClose={onClose} title="Новый заказ"
      footer={<Button block disabled={!ready} loading={create.isPending} loadingText="Заводим…" onClick={() => create.mutate()}>Завести заказ</Button>}>
      <div className="flex flex-col gap-3">
        <TextField label="Что делаем" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="Автоматизация склада" autoFocus />
        <SelectField label="Клиент" value={client} onChange={setClient} placeholder={clients.isLoading ? "Загружаем…" : "Выбери клиента"}
          options={(clients.data ?? []).filter((c) => !c.archived_at).map((c) => ({ value: c.id, label: c.name }))}
          hint={clients.data && clients.data.length === 0 ? "Клиентов ещё нет — заведи его на вкладке «Клиенты»." : undefined} />
        <SelectField label="Направление" value={area || onlyArea} onChange={setArea}
          options={(areas.data ?? []).map((a) => ({ value: a.id, label: a.name }))}
          hint={areas.data && areas.data.length === 0 ? "Нет направления, где бывают заказы." : undefined} />
        <SelectField label="Менеджер" value={manager} onChange={setManager} placeholder="Пока никто"
          options={(staff.data ?? []).map((a) => ({ value: a.id, label: a.name }))} />
        <SelectField label="Отвечает за заказ" value={owner} onChange={setOwner} placeholder="Я"
          options={(people.data ?? []).filter((p) => p.is_active).map((p) => ({ value: p.id, label: p.display_name }))} />
        <TextArea label="Что хочет клиент" value={brief} onChange={(e) => setBrief(e.target.value)} maxLength={20000}
          hint="Люди клиента и их контакты — в паспорте клиента." />
      </div>
    </Sheet>
  );
}

/* ── «Клиенты» ──────────────────────────────────────────────────────────────────────── */

export function ClientsBlock() {
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const clients = useQuery({ queryKey: ["clients", { archived }], queryFn: () => Clients.list(archived) });
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (clients.data ?? []).filter((c) => !needle || c.name.toLowerCase().includes(needle) || (c.inn ?? "").includes(needle));
  }, [clients.data, q]);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <SearchField value={q} onChange={setQ} placeholder="Название или ИНН" icon={<Search className="h-4 w-4 text-p-text-muted" aria-hidden="true" />} />
        </div>
        <Button size="compact" icon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={() => setCreating(true)}>Новый клиент</Button>
      </div>
      <label className="flex min-h-11 items-center gap-3 text-body text-p-text">
        <Switch on={archived} label="Показать клиентов в архиве" onChange={() => setArchived(!archived)} />
        С архивными
      </label>
      {clients.isLoading && <SkeletonList count={3} />}
      {clients.isError && <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void clients.refetch()}>Повторить</Button>}>Клиенты не загрузились.</EmptyNote>}
      {clients.data && shown.length === 0 && (
        <EmptyNote>{q ? "Никого не нашли." : "Клиентов пока нет. Клиент — организация; один клиент может заказывать в разных направлениях."}</EmptyNote>
      )}
      {shown.length > 0 && (
        <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
          {shown.map((c) => (
            <li key={c.id} className="border-b border-p-border last:border-0">
              <Link to={`/clients/${c.id}`} className="flex flex-col gap-0.5 px-4 py-3 press-surface">
                <span className="flex items-baseline gap-2">
                  <span className={cn("min-w-0 flex-1 truncate text-[15px] font-semibold", c.archived_at ? "text-p-text-muted" : "text-p-text")}>{c.name}</span>
                  {c.archived_at && <Mark>в архиве</Mark>}
                </span>
                <span className="text-meta text-p-text-muted">{[c.inn && `ИНН ${c.inn}`, ordersWord(c.orders)].filter(Boolean).join(" · ")}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <ClientSheet open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

/** «нет заказов», «1 заказ», «3 заказа», «5 заказов». */
export function ordersWord(n: number): string {
  if (n === 0) return "нет заказов";
  const d = n % 10;
  const h = n % 100;
  const word = d === 1 && h !== 11 ? "заказ" : d >= 2 && d <= 4 && (h < 12 || h > 14) ? "заказа" : "заказов";
  return `${n} ${word}`;
}

/** A client's card to make or change; its people in full live in its passport (Spec 054, step 3). */
function ClientSheet({ open, onClose, client }: { open: boolean; onClose: () => void; client?: ClientOut }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(client?.name ?? "");
  const [inn, setInn] = useState(client?.inn ?? "");
  const [about, setAbout] = useState(client?.about_md ?? "");
  const [contact, setContact] = useState(client?.contact_ref ?? "");
  const innError = inn.trim() && !/^(\d{10}|\d{12})$/.test(inn.trim()) ? "ИНН — 10 или 12 цифр" : undefined;
  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), inn: inn.trim() || null, about_md: about.trim() || null, contact_ref: contact.trim() || null };
      return client ? Clients.update(client.id, { ...body, version: client.version }) : Clients.create(body);
    },
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: ["clients"] });
      void qc.invalidateQueries({ queryKey: ["client", saved.id] });
      toast.success(client ? "Сохранено" : `${saved.name}: клиент заведён`);
      onClose();
      if (!client) navigate(`/clients/${saved.id}`);
    },
    onError: (error: Error) => toast.error(error instanceof VersionConflictError ? "Карточку клиента изменили. Закрой и открой снова." : error.message),
  });
  return (
    <Sheet open={open} onClose={onClose} title={client ? "Клиент" : "Новый клиент"}
      footer={<Button block disabled={!name.trim() || Boolean(innError)} loading={save.isPending} loadingText="Сохраняем…" onClick={() => save.mutate()}>{client ? "Сохранить" : "Завести клиента"}</Button>}>
      <div className="flex flex-col gap-3">
        <TextField label="Организация" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} placeholder="ООО Ромашка" autoFocus
          hint="Как называем клиента в работе: по этому имени методы проверяются на данные клиента." />
        <TextField label="ИНН" value={inn} onChange={(e) => setInn(e.target.value)} inputMode="numeric" maxLength={12} error={innError} />
        <TextArea label="Чем занимается, что болит" value={about} onChange={(e) => setAbout(e.target.value)} maxLength={5000} />
        <TextField label="Контакт" value={contact} onChange={(e) => setContact(e.target.value)} maxLength={200} placeholder="Иван Петров, директор, +7 …"
          hint="Кто на связи и как с ним связаться. Все люди клиента и их роли — в паспорте." />
        {!client && <p className="text-meta text-p-text-muted">Физлицо пока завести нельзя: для этого нужен узел в России (152-ФЗ).</p>}
      </div>
    </Sheet>
  );
}

/* ── One client ─────────────────────────────────────────────────────────────────────── */

export function ClientScreenRoute() {
  useBackButton("/projects?tab=clients");
  const { client_id } = useParams<{ client_id: string }>();
  return client_id ? <ClientScreen clientId={client_id} /> : null;
}

function ClientScreen({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const client = useQuery({ queryKey: ["client", clientId], queryFn: () => Clients.get(clientId) });
  const orders = useQuery({ queryKey: [...ORDERS_KEY, { client: clientId }], queryFn: () => Orders.list({ client_id: clientId }) });
  const archive = useMutation({
    mutationFn: (c: ClientOut) => Clients.update(c.id, { version: c.version, archived: !c.archived_at }),
    onSuccess: (c) => { toast.success(c.archived_at ? "Клиент в архиве" : "Клиент снова в работе"); void qc.invalidateQueries({ queryKey: ["clients"] }); void qc.invalidateQueries({ queryKey: ["client", clientId] }); },
    onError: (error: Error) => toast.error(error.message),
  });
  const c = client.data;
  return (
    <div className="flex max-w-[760px] flex-col gap-3">
      <AppBar fallback="/projects?tab=clients" context="Клиенты" />
      {client.isLoading && <SkeletonList count={2} />}
      {client.isError && <EmptyNote>Клиент не найден.</EmptyNote>}
      {c && (
        <>
          <ScreenHeader title={c.name} sub={[c.inn && `ИНН ${c.inn}`, c.archived_at && "в архиве"].filter(Boolean).join(" · ") || undefined}
            trailing={<Button size="compact" variant="secondary" onClick={() => setEditing(true)}>Изменить</Button>} />
          {c.about_md && <p className="whitespace-pre-wrap text-body text-p-text">{c.about_md}</p>}
          {c.contact_ref && <p className="text-meta text-p-text-muted">Контакт: {c.contact_ref}</p>}
          <div className="flex flex-wrap gap-2">
            {!c.archived_at && <Button size="compact" icon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={() => setOrdering(true)}>Новый заказ</Button>}
            <Button size="compact" variant="ghost" loading={archive.isPending} onClick={() => archive.mutate(c)}>{c.archived_at ? "Вернуть из архива" : "В архив"}</Button>
          </div>
          <section className="flex flex-col gap-2">
            <Caption>Паспорт</Caption>
            <ClientPassportBlock clientId={c.id} />
          </section>
          <section className="flex flex-col gap-2">
            <Caption>Досье</Caption>
            <ClientDossierBlock clientId={c.id} />
          </section>
          <section className="flex flex-col gap-2">
            <Caption>Доступы</Caption>
            <ClientAccessesBlock clientId={c.id} />
          </section>
          <section className="flex flex-col gap-2">
            <Caption>Документы</Caption>
            <ClientDocumentsBlock clientId={c.id} />
          </section>
          <section className="flex flex-col gap-2">
            <Caption>Заказы</Caption>
            {orders.isLoading && <SkeletonList count={2} />}
            {orders.data && orders.data.length === 0 && <EmptyNote>У клиента пока нет заказов.</EmptyNote>}
            {(orders.data ?? []).map((o) => <OrderCard key={o.id} order={o} />)}
          </section>
          <ClientSheet key={`${c.id}-${c.version}`} open={editing} onClose={() => setEditing(false)} client={c} />
          <NewOrderSheet open={ordering} onClose={() => setOrdering(false)} clientId={c.id} />
        </>
      )}
    </div>
  );
}

/** For «Решить» and the order's page: the stage as a quiet word with its place in the funnel. */
export function stageLine(o: Pick<OrderOut, "stage" | "lost_reason">): string {
  return o.stage === "lost" && o.lost_reason ? `${STAGE_LABEL.lost}: ${o.lost_reason}` : STAGE_LABEL[o.stage];
}
